/**
 * The sentence an operator reads when a signage save is refused.
 *
 * Every signage scope guards a save with `signageContentGuard` and answers a
 * refusal as `400 { error, assetId, blockers }`. The CMS used to drop all of
 * that on the floor and print "Could not save the playlist. Try again." - which
 * is actively wrong advice: trying again changes nothing, because every one of
 * these refusals is a property of the CONTENT, not of the request. The server
 * already knew which title and why; this is the component that was keeping it
 * to itself.
 *
 * Two of the reasons are re-packaging problems that belong to whoever runs the
 * catalogue, and the text says so, because the operator's useful next move is
 * to tell someone rather than to fiddle with the playlist.
 */

/** The shape `unwrap` throws: `code` is the server's `error`, `body` the rest. */
interface ThrownRefusal {
  code?: string;
  message?: string;
  body?: {
    error?: string;
    assetId?: string;
    blockers?: unknown[];
    webUrl?: string;
  };
}

/** One blocker code from `@shared/signage-content`, as a plain sentence. */
export function signageBlockerText(code: string): string {
  switch (code) {
    case "encrypted_not_licensable":
      return "it is protected and has no DASH version, which is the only format a licence can be issued for. The title needs re-packaging.";
    case "encrypted_no_content_id":
      return "it is protected but carries no DRM content id, so no licence can be issued for it. The title needs re-packaging.";
    case "no_playable_manifest":
      return "it has no streamable version (HLS or DASH). Ask for a rendition to be added.";
    case "not_playable":
      return "the catalogue has no media a player can use for it.";
    default:
      return `the catalogue refused it (${code}).`;
  }
}

/**
 * Turn a thrown save error into what the operator should read.
 *
 * `nameOf` lets the caller name the title the way the operator knows it (the
 * channel's name) instead of by its catalogue id; the id is the fallback,
 * because an id is still more useful than "one of your items".
 */
export function signageSaveErrorText(
  err: unknown,
  fallback: string,
  nameOf?: (assetId: string) => string | undefined
): string {
  const e = (err ?? {}) as ThrownRefusal;
  const code = e.code ?? e.body?.error;
  const assetId = e.body?.assetId ?? "";
  const label = assetId ? `“${nameOf?.(assetId) ?? assetId}”` : "An item";

  switch (code) {
    case "content_not_playable_in_signage": {
      const first = e.body?.blockers?.[0] as { code?: unknown } | undefined;
      const blocker = typeof first?.code === "string" ? first.code : "";
      return `${label} cannot be played on a screen: ${signageBlockerText(blocker)} Nothing was saved.`;
    }
    case "content_not_found":
      return `${label} no longer exists in the catalogue. Remove it and save again.`;
    case "signage_url_unsafe":
      return e.body?.webUrl
        ? `A screen cannot be pointed at “${e.body.webUrl}”. Use a public http(s) address.`
        : "A web page item has no address. Enter one or remove the item.";
    default:
      return fallback;
  }
}
